import { useCallback, useEffect, useState } from "react";
import { Eye, FileText, Search, X } from "lucide-react";
import api, { getError } from "@/api";
import { PdfFileActions } from "@/components/PdfFileActions";

const operationDate = (document) => (
  document.operation_date
  || document.movement_date
  || document.snapshot?.movement_date
  || document.created_at
);

const displayOperationDate = (document, withTime = false) => new Date(
  operationDate(document),
).toLocaleString(
  "fr-FR",
  withTime ? { dateStyle: "medium", timeStyle: "short" } : { dateStyle: "medium" },
);

const periods = [
  ["all", "Toute la période"],
  ["today", "Aujourd’hui"],
  ["yesterday", "Hier"],
  ["this_week", "Cette semaine"],
  ["last_7_days", "Les 7 derniers jours"],
  ["this_month", "Ce mois-ci"],
  ["last_30_days", "Les 30 derniers jours"],
  ["last_90_days", "Les 90 derniers jours"],
  ["this_year", "Cette année"],
  ["custom", "Dates personnalisées"],
];

const documentTypes = [
  ["entree", "Entrée"],
  ["sortie", "Sortie"],
  ["transfert", "Transfert"],
  ["initial", "Stock initial"],
  ["invoice", "Facture"],
  ["delivery", "Livraison"],
];

const warehouseTypes = [
  ["principal", "Dépôt principal"],
  ["zone_conakry", "Zone Conakry"],
  ["zone_interieur", "Zone intérieur"],
  ["livreur", "Dépôt livreur"],
];

const compactParams = (values) => Object.fromEntries(
  Object.entries(values).filter(([, value]) => value),
);

export default function FilesPage() {
  const [documents, setDocuments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [zones, setZones] = useState([]);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    document_type: "",
    period: "all",
    date_from: "",
    date_to: "",
    zone_id: "",
    subzone: "",
    warehouse_type: "",
  });
  const selectedZone = zones.find((zone) => zone.zone_id === filters.zone_id);
  const load = useCallback(() => api.get("/documents", {
    params: compactParams({ ...filters, query }),
  })
    .then((response) => {
      setDocuments(response.data);
      setError("");
    })
    .catch((requestError) => setError(getError(requestError))), [filters, query]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.get("/zones")
      .then((response) => setZones(response.data))
      .catch((requestError) => setError(getError(requestError)));
  }, []);

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const updateLocation = (key, value) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
      ...(key === "zone_id" ? { subzone: "" } : {}),
    }));
  };

  const open = async (documentId) => {
    try {
      const response = await api.get(`/documents/${documentId}`);
      setSelected(response.data);
      setError("");
    } catch (requestError) {
      setError(getError(requestError));
    }
  };

  return (
    <div className="page-enter" data-testid="files-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">ARCHIVES DOCUMENTAIRES</p>
          <h1 data-testid="files-title">Fichiers</h1>
          <p data-testid="files-description">
            Chaque mouvement validé conserve son bon d’entrée, sortie ou transfert.
          </p>
        </div>
      </div>
      <div className="document-filter-grid" data-testid="documents-filter">
        <div className="filter-search">
          <Search aria-hidden="true" size={17} />
          <input
            data-testid="documents-search-input"
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && load()}
            placeholder="N° de bon ou type"
            value={query}
          />
        </div>
        <label>
          <span>Type</span>
          <select
            data-testid="documents-type-filter"
            onChange={(event) => updateFilter("document_type", event.target.value)}
            value={filters.document_type}
          >
            <option value="">Tous les types</option>
            {documentTypes.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Période</span>
          <select
            data-testid="documents-period-filter"
            onChange={(event) => updateFilter("period", event.target.value)}
            value={filters.period}
          >
            {periods.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Zone</span>
          <select
            data-testid="documents-zone-filter"
            onChange={(event) => updateLocation("zone_id", event.target.value)}
            value={filters.zone_id}
          >
            <option value="">Toutes les zones</option>
            {zones.map((zone) => (
              <option key={zone.zone_id} value={zone.zone_id}>{zone.name}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Sous-zone</span>
          <select
            data-testid="documents-subzone-filter"
            disabled={!filters.zone_id}
            onChange={(event) => updateLocation("subzone", event.target.value)}
            value={filters.subzone}
          >
            <option value="">Toutes les sous-zones</option>
            {selectedZone?.subzones.map((subzone) => (
              <option key={subzone} value={subzone}>{subzone}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Type de dépôt</span>
          <select
            data-testid="documents-warehouse-type-filter"
            onChange={(event) => updateFilter("warehouse_type", event.target.value)}
            value={filters.warehouse_type}
          >
            <option value="">Tous les dépôts</option>
            {warehouseTypes.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </label>
        {filters.period === "custom" && (
          <>
            <label>
              <span>Du</span>
              <input
                data-testid="documents-date-from-filter"
                onChange={(event) => updateFilter("date_from", event.target.value)}
                type="date"
                value={filters.date_from}
              />
            </label>
            <label>
              <span>Au</span>
              <input
                data-testid="documents-date-to-filter"
                onChange={(event) => updateFilter("date_to", event.target.value)}
                type="date"
                value={filters.date_to}
              />
            </label>
          </>
        )}
        <button
          className="secondary-action"
          data-testid="documents-search-button"
          onClick={load}
          type="button"
        >
          Rechercher
        </button>
      </div>
      {error && <div className="notice-error" data-testid="files-error-message">{error}</div>}
      <section className="data-panel" data-testid="documents-list-panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">BIBLIOTHÈQUE</p>
            <h2>{documents.length} document(s)</h2>
          </div>
        </div>
        <div className="responsive-table">
          <table data-testid="documents-table">
            <thead>
              <tr>
                <th>Document</th>
                <th>Opération</th>
                <th>Dépôt</th>
                <th>Date d’opération</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {documents.map((item) => (
                <tr data-testid={`document-row-${item.document_id}`} key={item.document_id}>
                  <td>
                    <strong>{item.document_number}</strong>
                    <span>{item.title}</span>
                  </td>
                  <td>
                    <span className={`movement-badge ${item.movement_type}`}>
                      {item.movement_type}
                    </span>
                  </td>
                  <td>{item.snapshot.source_name}</td>
                  <td data-testid={`document-operation-date-${item.document_id}`}>
                    {displayOperationDate(item)}
                  </td>
                  <td>
                    <button
                      data-testid={`view-document-${item.document_id}`}
                      onClick={() => open(item.document_id)}
                      title="Ouvrir le document"
                      type="button"
                    >
                      <Eye size={17} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!documents.length && (
          <div className="empty-inline" data-testid="empty-documents-message">
            <FileText size={24} />
            Les documents générés seront visibles ici.
          </div>
        )}
      </section>
      {selected && <DocumentPreview data={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function DocumentPreview({ data, onClose }) {
  const { document, company } = data;
  const getPdfBlob = async () => {
    const response = await api.get(`/documents/${document.document_id}/pdf`, {
      responseType: "blob",
    });
    return response.data;
  };
  return (
    <div className="document-modal" data-testid="document-preview-modal">
      <div className="document-paper" data-testid="document-paper-preview">
        <header>
          <div>
            <strong data-testid="document-company-name">{company.name}</strong>
            <span>{company.legal_name}</span>
            <span>{company.address} · {company.phone}</span>
          </div>
          <button
            data-testid="close-document-preview-button"
            onClick={onClose}
            title="Fermer l’aperçu"
            type="button"
          >
            <X size={18} />
          </button>
        </header>
        <div className="document-title">
          <span>{document.title}</span>
          <strong data-testid="document-number">{document.document_number}</strong>
        </div>
        <div className="document-meta">
          <span>Émetteur : {document.snapshot.source_name}</span>
          <span data-testid="document-preview-operation-date">
            Date d’opération : {displayOperationDate(document, true)}
          </span>
        </div>
        <table data-testid="document-lines-table">
          <thead>
            <tr>
              <th>Produit</th>
              <th>Quantité</th>
              <th>Prix de vente</th>
            </tr>
          </thead>
          <tbody>
            {document.snapshot.lines.map((line, index) => (
              <tr key={`${line.product_id}-${index}`}>
                <td>{line.product_name}</td>
                <td>{line.quantity}</td>
                <td>{line.sale_price.toLocaleString("fr-FR")} GNF</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="document-signatures">
          <div>
            <span>Émetteur</span>
            <strong>{document.snapshot.author_name}</strong>
          </div>
          <div>
            <span>Récepteur</span>
            <strong>Signature et cachet</strong>
          </div>
        </div>
        <footer className="document-actions">
          <PdfFileActions
            fileName={`${document.document_number}.pdf`}
            getPdfBlob={getPdfBlob}
            testIdPrefix="archive-document"
          />
        </footer>
      </div>
    </div>
  );
}